import { Component, OnInit, OnDestroy, inject, ViewChild, TemplateRef, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';
import { Global } from '@services/core/Global';
import { PreviewClaimComponent } from '../../../management-claim/components/preview-claim/preview-claim.component';
import { ClaimInformePdfComponent } from '../../../management-claim/components/claim-informe-pdf/claim-informe-pdf.component';
import { MembershipInformePdfComponent } from '../../components/membership-informe-pdf/membership-informe-pdf.component';
import { CatalogService } from '@services/data/catalog.service';

@Component({
  selector: 'app-confirm-request',
  standalone: true,
  imports: [CommonModule, FormsModule, PreviewClaimComponent, ClaimInformePdfComponent, MembershipInformePdfComponent],
  templateUrl: './confirm-request.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./confirm-request.component.scss']
})
export class ConfirmRequestComponent implements OnInit, OnDestroy {
  @Input() isPublic: boolean = true;
  @Input() inputToken: string | null = null;

  public token: string | null = null;
  public isLoading = true;
  public isValid = false;
  public isProcessed = false;
  public isExpired = false;
  public requestData: any = null;
  public isSubmitting = false;
  public pdfSrc: SafeResourceUrl | null = null;
  public confirmedDate: string | null = null;
  public timeLeftStr: string = '';
  private timerId: any = null;
  private paramSub: Subscription | null = null;

  private catalogService = inject(CatalogService);

  @ViewChild('pdfModal') pdfModal!: TemplateRef<any>;
  @ViewChild('membershipPdf') membershipPdf!: MembershipInformePdfComponent;
  @ViewChild('claimInforme') claimInforme?: ClaimInformePdfComponent;

  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);
  private apiUrl = `${Global.url}V1`;

  get processSlug(): string {
    return this.requestData?.processSlug || this.requestData?.process?.slug || '';
  }

  get processTitle(): string {
    return this.requestData?.process?.name ?? 'Solicitud';
  }

  get expressApprovalTitle(): string {
    const role = this.requestData?.stepRoleName ?? this.requestData?.roleLabel;
    return role ? `Aprobación Express — ${role}` : `Aprobación Express — ${this.processTitle}`;
  }

  private processCategory(): string {
    const fromReq = this.requestData?.process?.category
      ?? this.requestData?.processCategory
      ?? this.catalogService.processes().find((p: any) => p.slug === this.processSlug)?.category;
    return String(fromReq || '').trim().toUpperCase();
  }

  get isClaimProcess(): boolean {
    if (this.requestData?.claim?.id || this.requestData?.claim) return true;
    const cat = this.processCategory();
    if (cat === 'CLAIM') return true;
    const slug = String(this.processSlug || '').toUpperCase();
    return ['ACCIDENT', 'TOTAL_LOSS', 'CLAIM'].some((s) => slug.includes(s));
  }

  get isUnsubscribe(): boolean {
    if (this.isClaimProcess) return false;
    const cat = this.processCategory();
    const slug = String(this.processSlug || '').toUpperCase();
    return cat === 'UNSUBSCRIBE' || cat === 'UNSUBSCRIBE_PARTY' || cat === 'UNSUBSCRIBE_ADM'
      || slug === 'UNSUBSCRIBE' || slug.includes('UNSUBSCRIBE') || slug.includes('BAJA');
  }

  get isMembershipProcess(): boolean {
    if (this.isClaimProcess || this.isUnsubscribe || this.isVehicleUpdate) return false;
    const cat = this.processCategory();
    return cat === 'MEMBERSHIP' || cat === 'VEHICLE';
  }

  get isVehicleUpdate(): boolean {
    if (this.isClaimProcess || this.isUnsubscribe) return false;
    const cat = this.processCategory();
    if (cat === 'VEHICLE_UPDATE') return true;
    const slug = String(this.processSlug || '').toUpperCase();
    return slug === 'VEHICLE_QUICK_UPDATE' || slug === 'VEHICLE_DATA_UPDATE';
  }

  get proposedChangeRows(): Array<{ key: string; label: string; from: any; to: any }> {
    const raw = this.requestData?.proposedChanges ?? this.requestData?.proposed_changes;
    if (!raw || typeof raw !== 'object') return [];
    const labels: Record<string, string> = {
      plate: 'Patente',
      year: 'Año',
      brandCode: 'Marca (código)',
      modelCode: 'Modelo (código)',
      vin: 'N° motor / VIN',
      value: 'Valor compra',
      commercialValue: 'Valor comercial',
      color: 'Color',
      km: 'Kilometraje'
    };
    return Object.entries(raw).map(([key, val]: [string, any]) => ({
      key,
      label: labels[key] || key,
      from: val?.from ?? '—',
      to: val?.to ?? '—'
    }));
  }

  get claimData(): any {
    return this.requestData?.claim ?? null;
  }

  get partyData(): any {
    const party = this.requestData?.party ?? this.requestData?.claim?.party ?? null;
    if (!party) return null;
    const entity = party.entity ?? this.requestData?.entity ?? null;
    return {
      ...party,
      entity,
      entityCode: party.entityCode ?? entity?.code ?? this.requestData?.entityCode ?? null
    };
  }

  /** Payload listo para el informe: party/entity/vehicle/details del token, sin hydrate. */
  private _informeContext(): { claim: any; party: any; skipHydrate: true } {
    const request = this.requestData;
    const raw = this.claimData;
    const details = Array.isArray(raw?.details) ? raw.details : [];
    const vehicle = raw?.vehicle ?? request?.vehicle ?? null;
    const claim = raw
      ? { ...raw, request, vehicle, details }
      : null;
    return { claim, party: this.partyData, skipHydrate: true };
  }

  get isClaimWide(): boolean {
    return !this.isLoading && this.isValid && !this.isExpired && this.isClaimProcess;
  }

  /** La ruta pública no debe depender del @Input isPublic (el router puede pisarlo). */
  get isPublicView(): boolean {
    return this.isPublic !== false;
  }

  ngOnInit(): void {
    this.paramSub = this.route.queryParamMap.subscribe((params) => {
      const next = this.inputToken || params.get('token');
      if (next === this.token && this.requestData) return;
      this.token = next;
      this._resetView();
      if (this.token) {
        this.loadRequestDetails();
      } else {
        this.isLoading = false;
      }
    });
  }

  private _resetView(): void {
    this.isLoading = true;
    this.isValid = false;
    this.isExpired = false;
    this.isProcessed = false;
    this.requestData = null;
    this.confirmedDate = null;
    this.timeLeftStr = '';
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  loadRequestDetails(): void {
    this.http.get<any>(`${this.apiUrl}/requests/confirm-token/${this.token}`).subscribe({
      next: (res) => this._onDetailsSuccess(res.data ?? res),
      error: () => this._onDetailsError()
    });
  }

  private _onDetailsSuccess(data: any): void {
    this.isLoading = false;
    this.isValid = data.isValid;
    this.isExpired = data.isExpired;
    this.requestData = data.request;
    this.isProcessed = this._computeIsProcessed();
    this._initCountdown();
    if (this.isClaimWide && this.claimData) {
      setTimeout(() => this._generateClaimInforme());
    }
  }

  private async _generateClaimInforme(attempt = 0): Promise<void> {
    if (!this.claimData) return;
    if (!this.claimInforme) {
      if (attempt < 20) setTimeout(() => this._generateClaimInforme(attempt + 1), 100);
      return;
    }
    await this.claimInforme.generate(this._informeContext());
  }

  openClaimInforme(): void {
    if (!this.claimInforme) return;
    if (this.claimInforme.pdfSrcUrl()) {
      this.claimInforme.openGeneratedModal();
      return;
    }
    void this.claimInforme.openPreview(this._informeContext());
  }

  private _computeIsProcessed(): boolean {
    const status = this.requestData?.statusSlug;
    if (!status) return false;
    if (this.isClaimProcess) return status !== 'PENDING';
    if (this.isUnsubscribe) return !['DRAFT', 'PENDING'].includes(status);
    if (this.isVehicleUpdate) return !['DRAFT', 'PENDING'].includes(status);
    return ['APPROVED', 'REJECTED', 'CLOSED'].includes(status);
  }

  private _initCountdown(): void {
    if (this.isValid && !this.isExpired && this.requestData?.tokenExpiresAt) {
      this.startCountdown(this.requestData.tokenExpiresAt);
    }
  }

  private _onDetailsError(): void {
    this.isLoading = false;
    this.isValid = false;
  }

  ngOnDestroy(): void {
    if (this.timerId) clearInterval(this.timerId);
    this.paramSub?.unsubscribe();
  }

  startCountdown(expiresAt: string): void {
    this._updateTimeLeft(expiresAt);
    this.timerId = setInterval(() => this._updateTimeLeft(expiresAt), 1000);
  }

  private _updateTimeLeft(expiresAt: string): void {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) {
      this._handleExpiration();
      return;
    }
    this.timeLeftStr = this._formatTime(diff);
  }

  private _handleExpiration(): void {
    this.isExpired = true;
    this.isValid = false;
    this.timeLeftStr = 'Expirado';
    if (this.timerId) clearInterval(this.timerId);
  }

  private _formatTime(ms: number): string {
    const hrs = Math.floor(ms / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${hrs}h ${mins}m ${secs}s`;
  }

  openMembershipPdf(): void {
    this.membershipPdf?.openPreview();
  }

  confirmAction(action: 'CONFIRM' | 'REJECT'): void {
    if (action === 'REJECT') {
      const title = this.isClaimProcess ? 'Rechazar siniestro' : (this.isMembershipProcess ? 'Rechazar Incorporación' : 'Rechazar Solicitud');
      Swal.fire({
        title, text: 'Por favor indique el motivo de su rechazo:', input: 'textarea',
        inputValidator: (v) => !v ? 'El motivo es obligatorio' : null,
        showCancelButton: true, confirmButtonText: 'Enviar Rechazo', cancelButtonText: 'Cancelar'
      }).then((res) => { if (res.isConfirmed && res.value) this._executeConfirmation(action, res.value); });
      return;
    }
    const title = this.isClaimProcess ? '¿Aprobar solicitud?' : (this.isMembershipProcess ? '¿Aprobar Incorporación?' : '¿Confirmar y Autorizar Baja?');
    const text = this.isClaimProcess ? 'La solicitud avanzará en el flujo de aprobación del FPV.' : (this.isMembershipProcess ? 'Se aprobará la solicitud de incorporación del socio y vehículo.' : 'Se procesará la solicitud de desafiliación de la cobertura FPV.');
    const btnText = this.isClaimProcess ? 'Sí, aprobar' : (this.isMembershipProcess ? 'Sí, Aprobar' : 'Sí, Confirmar Baja');
    Swal.fire({ title, text, icon: 'question', showCancelButton: true, confirmButtonText: btnText, cancelButtonText: 'Cancelar' })
      .then((res) => { if (res.isConfirmed) this._executeConfirmation(action); });
  }

  openPdfModal(contentModal?: any): void {
    const doc = this._buildPDFDocument();
    const blob = doc.output('blob');
    const blobUrl = URL.createObjectURL(blob);
    this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
    this.modalService.open(contentModal || this.pdfModal, { size: 'lg', backdrop: 'static' });
  }

  downloadPDF(): void {
    const doc = this._buildPDFDocument();
    doc.save(`Solicitud_Baja_FPV_${this.requestData?.vehicle?.plate || 'Documento'}.pdf`);
  }

  private _buildPDFDocument(): jsPDF {
    const doc = new jsPDF('p', 'pt', 'letter');
    const pdfWidth = doc.internal.pageSize.getWidth() - 35;
    const entityName = this.requestData?.entity?.name ? `${this.requestData?.entity?.code} - ${this.requestData?.entity?.name}` : 'ENTIDAD FPV';

    autoTable(doc, {
      html: '#hidden-baja-table', startY: 105, theme: 'grid', useCss: true,
      margin: { top: 110, bottom: 80, left: 35, right: 35 }
    });

    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this._addHeaderPDF(doc, entityName, 'SOLICITUD DE BAJA DE COBERTURA');
      this._addFooterPDF(doc, pdfWidth, i, totalPages);
    }

    return doc;
  }

  private _addHeaderPDF(doc: jsPDF, entityName: string, title: string): number {
    let currentY = 30;
    autoTable(doc, {
      startY: currentY,
      head: [
        [{ content: 'Fondo de Protección Vehicular', styles: { fontStyle: 'normal', fontSize: 10 } }],
        [{ content: entityName, styles: { fontStyle: 'italic', fontSize: 10 } }]
      ],
      theme: 'plain', headStyles: { halign: 'center', cellPadding: 1, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 }
    });

    autoTable(doc, {
      startY: currentY + 45,
      head: [[{ content: title, styles: { fontStyle: 'bold', fontSize: 11 } }]],
      theme: 'plain', headStyles: { halign: 'center', cellPadding: 0.3, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 }
    });
    return currentY;
  }

  private _addFooterPDF(doc: jsPDF, pdfWidth: number, pageNumber: number, totalPages: number): void {
    const todayStr = `${new Date().getDate().toString().padStart(2, '0')}/${(new Date().getMonth() + 1).toString().padStart(2, '0')}/${new Date().getFullYear()}`;
    autoTable(doc, {
      startY: 742,
      body: [['Impreso: ' + todayStr, 'Sistema: FPV', 'Página ' + pageNumber + ' de ' + totalPages]],
      theme: 'plain', bodyStyles: { fontSize: 7, cellPadding: 0.3, textColor: [0, 0, 0] },
      didDrawPage: () => {
        doc.setLineWidth(0.5);
        doc.line(35, 737, pdfWidth, 737);
      }
    });
  }

  private _executeConfirmation(action: 'CONFIRM' | 'REJECT', comments = ''): void {
    this.isSubmitting = true;
    const body = { token: this.token, action, comments };
    this.http.post<any>(`${this.apiUrl}/requests/confirm-token`, body).subscribe({
      next: (res) => this._handleConfirmSuccess(res, action),
      error: (err) => this._handleConfirmError(err)
    });
  }

  // Procesa exito de confirmacion y notifica via toast
  private _handleConfirmSuccess(res: any, action: 'CONFIRM' | 'REJECT'): void {
    this.isSubmitting = false;
    this.isProcessed = true;
    const resData = res?.data ?? res;
    this.confirmedDate = resData?.confirmedAt ? new Date(resData.confirmedAt).toLocaleString('es-CL') : new Date().toLocaleString('es-CL');
    this._updateConfirmedRequest(action, resData?.confirmedAt);
    this._showConfirmationToast(action);
  }

  // Actualiza estado local de la solicitud
  private _updateConfirmedRequest(action: 'CONFIRM' | 'REJECT', confirmedAt?: any): void {
    if (!this.requestData) return;
    const nextStatus = action === 'CONFIRM' ? (this.isClaimProcess ? 'MINISTRY_APPROVED' : 'APPROVED') : 'REJECTED';
    this.requestData = { ...this.requestData, statusSlug: nextStatus, confirmedAt };
  }

  // Maneja error al confirmar
  private _handleConfirmError(err: any): void {
    this.isSubmitting = false;
    Swal.fire('Error', err.error?.error ?? err.error?.message ?? 'No se pudo procesar su respuesta', 'error');
  }

  // Muestra toast no invasivo superior
  private _showConfirmationToast(action: 'CONFIRM' | 'REJECT'): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: action === 'CONFIRM' ? 'success' : 'info',
      title: this._successTitle(action),
      text: this._successText(action),
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true
    });
  }

  private _successTitle(action: 'CONFIRM' | 'REJECT'): string {
    if (action !== 'CONFIRM') return 'Solicitud Rechazada';
    if (this.isClaimProcess) return 'Aprobación registrada';
    return this.isMembershipProcess ? '¡Incorporación Aprobada!' : '¡Baja Confirmada!';
  }

  private _successText(action: 'CONFIRM' | 'REJECT'): string {
    if (this.isClaimProcess) {
      return action === 'CONFIRM' ? `Aprobación registrada el ${this.confirmedDate}.` : 'Ha rechazado la solicitud.';
    }
    if (this.isMembershipProcess) {
      return action === 'CONFIRM' ? `Incorporación aprobada exitosamente el ${this.confirmedDate}.` : 'Ha rechazado la solicitud de incorporación.';
    }
    return action === 'CONFIRM' ? `Respuesta registrada el ${this.confirmedDate}.` : 'Ha rechazado la solicitud de baja.';
  }

  getCurrentDay(): number {
    return new Date().getDate();
  }
}
